import { useEffect, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { BLUE } from './tokens'
import type { ValidationError } from './types'

// Shared wizard chrome — the top bar (Udfyld/Forhåndsvis tabs, save, menu,
// Send + validation), the numbered step rail, and the body switch (form
// section ↔ PDF preview). Both the residential (A9/A10) wizard and the erhverv
// wizard render through THIS component so the two are visually identical by
// construction. Lifted from the original WizardShell so it is pixel-for-pixel
// the same; do not fork it — change it here for both.

export type StepStatus = 'done' | 'partial' | 'active' | 'future'

// ── SVG icons ────────────────────────────────────────────────────────────────
const IconPencil = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
)
const IconDoc = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
)
const IconZoomOut = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    <line x1="8" y1="11" x2="14" y2="11"/>
  </svg>
)
const IconZoomIn = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
  </svg>
)
const IconSend = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="white" stroke="none"/>
  </svg>
)
const IconDots = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
  </svg>
)
const IconStar = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
)

const iconBtnBase: React.CSSProperties = {
  width: 32, height: 32, border: '1px solid #e8eaed', borderRadius: 7,
  background: 'white', cursor: 'pointer', color: '#6b7280',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
}

interface Props {
  navSteps: string[]
  sections: React.ReactNode[]
  currentSection: number
  stepStatus: (i: number) => StepStatus
  onSectionChange: (i: number) => void
  view: 'form' | 'preview'
  setView: (v: 'form' | 'preview') => void
  onPreview: () => void
  saving: boolean
  onNext: () => void
  onBack: () => void
  onSaveDraft: () => void
  onSaveDraftAndClose: () => void
  onDeleteDraft: () => void
  validationErrors: ValidationError[]
  onSend: () => void
  // preview wiring — render-prop so the chrome owns zoom and feeds it in
  renderPreview: (zoom: number) => React.ReactNode
  pdfUrl: string | null
  thumbPages: number
  // optional bits
  fullBleedSection?: number          // section index rendered full-bleed (e.g. §11)
  assistant?: React.ReactNode        // slide-out panel
  showAssistant?: boolean
  onToggleAssistant?: () => void
  onDuplicate?: () => void           // adds a "Dupliker kontrakt" menu item
  downloadName?: string
}

export function WizardChrome({
  navSteps, sections, currentSection, stepStatus, onSectionChange,
  view, setView, onPreview, saving, onNext, onBack,
  onSaveDraft, onSaveDraftAndClose, onDeleteDraft,
  validationErrors, onSend, renderPreview, pdfUrl, thumbPages,
  fullBleedSection, assistant, showAssistant, onToggleAssistant, onDuplicate,
  downloadName = 'lejekontrakt-udkast.pdf',
}: Props) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [zoom, setZoom] = useState(1.0)
  const [showMenu, setShowMenu] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const canSend = validationErrors.length === 0
  const isFullBleed = fullBleedSection !== undefined && currentSection === fullBleedSection

  const scrollToPage = (pageNum: number) => {
    setSelectedPage(pageNum)
    document.getElementById(`pdf-page-${pageNum}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {/* ── Top bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: 56, background: 'white',
        borderBottom: '1px solid #e8eaed', flexShrink: 0,
      }}>
        {isMobile && view === 'form' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
            <button onClick={onBack} disabled={currentSection === 0}
              style={{ background: 'none', border: 'none', cursor: currentSection === 0 ? 'default' : 'pointer', color: currentSection === 0 ? '#d1d5db' : '#374151', padding: '4px 6px', fontSize: 16, flexShrink: 0 }}>‹</button>
            <div style={{ minWidth: 0, flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1 }}>{currentSection + 1} / {navSteps.length}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{navSteps[currentSection]}</div>
            </div>
            <button onClick={onNext} disabled={currentSection === navSteps.length - 1 || saving}
              style={{ background: 'none', border: 'none', cursor: currentSection === navSteps.length - 1 ? 'default' : 'pointer', color: currentSection === navSteps.length - 1 ? '#d1d5db' : '#374151', padding: '4px 6px', fontSize: 16, flexShrink: 0 }}>›</button>
          </div>
        ) : (
          <div style={{ minWidth: 200, fontSize: 14, fontWeight: 600, color: '#111827' }}>
            {view === 'preview' ? 'Forhåndsvis' : navSteps[currentSection]}
          </div>
        )}

        {!isMobile && <div />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', background: '#f7f8fa', borderRadius: 8, padding: 3, marginRight: 4 }}>
            <button onClick={() => setView('form')} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: view === 'form' ? 'white' : 'transparent', color: view === 'form' ? '#111827' : '#9ca3af',
              fontWeight: view === 'form' ? 500 : 400, boxShadow: view === 'form' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s',
            }}>{IconPencil}{!isMobile && <span style={{ marginLeft: 5 }}>Udfyld</span>}</button>
            <button onClick={onPreview} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', fontSize: 13, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: view === 'preview' ? 'white' : 'transparent', color: view === 'preview' ? '#111827' : '#9ca3af',
              fontWeight: view === 'preview' ? 500 : 400, boxShadow: view === 'preview' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s',
            }}>{IconDoc}{!isMobile && <span style={{ marginLeft: 5 }}>Forhåndsvis</span>}</button>
          </div>

          {view === 'preview' && (
            <>
              <button onClick={() => setZoom(z => Math.max(0.5, parseFloat((z - 0.1).toFixed(1))))} style={iconBtnBase} title="Zoom ud">{IconZoomOut}</button>
              <button onClick={() => setZoom(z => Math.min(2.0, parseFloat((z + 0.1).toFixed(1))))} style={iconBtnBase} title="Zoom ind">{IconZoomIn}</button>
            </>
          )}

          {assistant && (
            <button onClick={onToggleAssistant} style={{
              ...iconBtnBase, background: showAssistant ? '#eef3ff' : 'white',
              color: showAssistant ? BLUE : '#4b5563', borderColor: showAssistant ? BLUE : '#e5e7eb',
            }} title="Kontraktassistent">{IconStar}</button>
          )}

          <button onClick={onSaveDraft} disabled={saving} style={iconBtnBase} title="Gem kladde">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
            </svg>
          </button>

          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowMenu(m => !m)} style={iconBtnBase}>{IconDots}</button>
            {showMenu && (
              <div style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 200, background: 'white',
                border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,.1)', minWidth: 200, overflow: 'hidden',
              }} onMouseLeave={() => setShowMenu(false)}>
                {pdfUrl && (
                  <a href={pdfUrl} download={downloadName} onClick={() => setShowMenu(false)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, color: '#374151', textDecoration: 'none', borderBottom: '1px solid #f3f4f6',
                  }}>↓ Hent kontrakt</a>
                )}
                {onDuplicate && (
                  <button onClick={() => { setShowMenu(false); onDuplicate() }} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, color: '#374151',
                    background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                  }}>⊕ Dupliker kontrakt</button>
                )}
                <button onClick={() => { setShowMenu(false); onDeleteDraft() }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, color: '#dc2626',
                  background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer',
                }}>✕ Slet kladde</button>
              </div>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <button onClick={() => { if (!canSend) { setShowValidation(v => !v); return } setShowValidation(false); onSend() }} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600,
              background: canSend ? BLUE : '#d1d5db', color: 'white', border: 'none', borderRadius: 7, cursor: canSend ? 'pointer' : 'not-allowed',
            }}>
              {IconSend}Send
              {!canSend && (
                <span style={{ background: '#ef4444', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                  {validationErrors.length}
                </span>
              )}
            </button>
            {showValidation && validationErrors.length > 0 && (
              <div style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 100, background: 'white',
                border: '1px solid #fca5a5', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,.12)', padding: '14px 16px', minWidth: 260,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 10 }}>
                  {validationErrors.length} {validationErrors.length === 1 ? 'felt mangler' : 'felter mangler'}
                </div>
                {validationErrors.map((e, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>Trin {e.step + 1} · {e.stepName}</span>
                    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{e.label}</span>
                  </div>
                ))}
                <button onClick={() => { onSectionChange(validationErrors[0].step); setShowValidation(false) }} style={{
                  marginTop: 10, width: '100%', fontSize: 12, padding: '7px', background: BLUE, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer',
                }}>Gå til første manglende felt</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <nav style={{
          width: isMobile ? 0 : 240, background: 'white', borderRight: isMobile ? 'none' : '1px solid #e8eaed',
          display: isMobile ? 'none' : 'flex', flexDirection: 'column', flexShrink: 0,
        }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: view === 'preview' ? '12px 10px' : '8px 10px' }}>
            {view === 'preview' && pdfUrl ? (
              <Document file={pdfUrl} loading={null}>
                {Array.from({ length: thumbPages }, (_, i) => (
                  <div key={i} onClick={() => scrollToPage(i + 1)} style={{
                    marginBottom: 10, cursor: 'pointer', padding: 3, borderRadius: 5,
                    border: `2px solid ${selectedPage === i + 1 ? BLUE : 'transparent'}`, transition: 'border-color 0.12s',
                  }}>
                    <div style={{ boxShadow: '0 1px 5px rgba(0,0,0,0.15)', lineHeight: 0, borderRadius: 2, overflow: 'hidden' }}>
                      <Page pageNumber={i + 1} width={244} renderTextLayer={false} renderAnnotationLayer={false} />
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center', marginTop: 5 }}>Side {i + 1}</div>
                  </div>
                ))}
              </Document>
            ) : (
              navSteps.map((stepLabel, i) => {
                const s = stepStatus(i)
                return (
                  <div key={i} onClick={() => onSectionChange(i)} style={{
                    display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 7, marginBottom: 1,
                    background: s === 'active' ? '#eef2ff' : 'transparent', cursor: 'pointer', transition: 'background 0.1s',
                  }}>
                    <StepIcon num={i + 1} status={s} />
                    <span style={{
                      fontSize: 13, color: s === 'done' ? '#16a34a' : s === 'active' ? BLUE : s === 'partial' ? '#d97706' : '#9ca3af',
                      fontWeight: s === 'active' ? 600 : 400,
                    }}>{stepLabel}</span>
                  </div>
                )
              })
            )}
          </div>
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e8eaed' }}>
            <button onClick={onSaveDraftAndClose} style={{
              width: '100%', fontSize: 12, color: '#6b7280', background: 'none',
              border: '1px solid #e8eaed', borderRadius: 7, padding: '7px 0', cursor: 'pointer',
            }}>Gem kladde & luk</button>
          </div>
        </nav>

        {view === 'form' ? (
          <div style={{ flex: 1, overflow: isFullBleed ? 'hidden' : 'auto', background: isFullBleed ? 'white' : '#f7f8fa', display: 'flex', flexDirection: 'column' }}>
            {isFullBleed ? (
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{sections[currentSection]}</div>
            ) : (
              <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 20px 80px', width: '100%' }}>{sections[currentSection]}</div>
            )}
            {!isMobile && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px',
                background: isFullBleed ? 'white' : 'transparent', borderTop: isFullBleed ? '1px solid #e8eaed' : 'none', flexShrink: 0,
              }}>
                <button onClick={onBack} disabled={currentSection === 0} style={{
                  fontSize: 13, color: currentSection === 0 ? '#d1d5db' : '#374151', background: 'white',
                  border: '1px solid #d1d5db', borderRadius: 7, padding: '9px 18px', cursor: currentSection === 0 ? 'default' : 'pointer',
                }}>← Forrige trin</button>
                {currentSection < navSteps.length - 1 && (
                  <button onClick={onNext} disabled={saving} style={{
                    fontSize: 13, fontWeight: 600, color: 'white', background: BLUE, border: 'none', borderRadius: 7, padding: '9px 22px', cursor: 'pointer',
                  }}>{saving ? 'Gemmer…' : 'Næste trin →'}</button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {renderPreview(zoom)}
          </div>
        )}

        {showAssistant && assistant}
      </div>
    </div>
  )
}

export function StepIcon({ num, status }: { num: number; status: StepStatus }) {
  const r = 9
  const cx = 12, cy = 12
  const circumference = 2 * Math.PI * r

  if (status === 'done') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="#16a34a" />
        <polyline points="8.5 12 11 14.5 15.5 9.5" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (status === 'active') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill={BLUE} />
        <text x={cx} y={cy + 4} textAnchor="middle" fill="white" fontSize="10" fontWeight="700">{num}</text>
      </svg>
    )
  }
  if (status === 'partial') {
    const fill = circumference * 0.42
    const empty = circumference - fill
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e8eaed" strokeWidth="2.5" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#d97706" strokeWidth="2.5"
          strokeDasharray={`${fill} ${empty}`} transform={`rotate(-90 ${cx} ${cy})`} />
        <text x={cx} y={cy + 4} textAnchor="middle" fill="#374151" fontSize="10">{num}</text>
      </svg>
    )
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#d1d5db" strokeWidth="2" />
      <text x={cx} y={cy + 4} textAnchor="middle" fill="#9ca3af" fontSize="10">{num}</text>
    </svg>
  )
}
